import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BadgeCheck, CalendarCheck, Clock, Loader2, Search } from "lucide-react";
import { api, formatINR } from "@/lib/api";

export default function BookingLookup() {
  const [ref, setRef] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!ref.trim()) return;
    setLoading(true);
    setBooking(null);
    try {
      const res = await api.get(`/bookings/reference/${encodeURIComponent(ref.trim())}`);
      setBooking(res.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("No booking found for that reference. Check and try again.");
      } else {
        toast.error("Lookup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full flex-1 rounded-xl border border-white/10 bg-[#111116] px-4 py-3.5 font-mono2 text-sm uppercase tracking-[0.1em] text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

  return (
    <div className="mx-auto mt-16 max-w-3xl" data-testid="booking-lookup">
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0e] p-6 sm:p-8">
        <p className="eyebrow">Booking Lookup</p>
        <h3 className="mt-2 font-display text-xl font-bold uppercase text-white">
          Already reserved? Track it
        </h3>
        <form onSubmit={lookup} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="lookup-ref-input"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="ISHU-XXXXXXXX"
            className={inputCls}
            aria-label="Booking reference"
          />
          <button
            type="submit"
            data-testid="lookup-button"
            disabled={loading || !ref.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#e6b800] px-7 py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
            {loading ? "Searching…" : "Track booking"}
          </button>
        </form>

        {booking && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-6 rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/5 p-5"
            data-testid="lookup-result"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono2 text-sm font-semibold tracking-[0.1em] text-white" data-testid="lookup-result-ref">
                {booking.reference}
              </p>
              {booking.status === "confirmed" ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#e6b800]/40 bg-[#e6b800]/10 px-3 py-1 text-[11px] font-semibold text-[#e6b800]"
                  data-testid="lookup-result-status"
                >
                  <BadgeCheck size={12} /> Confirmed
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-[11px] text-neutral-400"
                  data-testid="lookup-result-status"
                >
                  <Clock size={12} /> Payment pending
                </span>
              )}
              {booking.founding_number ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#e6b800]/50 bg-[#e6b800]/10 px-3 py-1 text-[11px] font-bold text-[#e6b800]"
                  data-testid="lookup-founding-badge"
                >
                  <BadgeCheck size={12} /> Founding Owner #{booking.founding_number}
                </span>
              ) : null}
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">Variant</p>
                <p className="mt-1 font-semibold text-white" data-testid="lookup-result-variant">
                  {booking.model ? `${booking.model} ` : ""}{booking.variant || ""}{booking.color ? ` · ${booking.color}` : ""}
                </p>
              </div>
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">Token</p>
                <p className="mt-1 text-neutral-300">
                  {booking.status === "confirmed" ? `${formatINR(booking.token_amount)} paid` : "Not paid yet"}
                </p>
              </div>
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">Test drive</p>
                <p className="mt-1 text-neutral-300" data-testid="lookup-result-testdrive">
                  {booking.test_drive ? (
                    <span className="inline-flex items-center gap-1.5 text-[#e6b800]">
                      <CalendarCheck size={13} /> {booking.test_drive.city}, {booking.test_drive.date}, {booking.test_drive.slot}
                    </span>
                  ) : (
                    "Not scheduled"
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}